/**
 * Theme token layer — "white treatment" (neutral phase).
 *
 * Scope (intentionally limited for this pass): we only emit the NEUTRAL token
 * set — the original app look (white chrome, faint-grey content canvas, card
 * depth, frameless) plus soft-grey accents. A community is "neutral" when it
 * has no effective brand color: `custom_branding` is null/absent OR its
 * `primaryColor` is white (#ffffff). Both collapse to the same neutral look.
 *
 * Communities with a real brand color are NOT handled here yet — they keep the
 * existing inline-style chrome coloring. The themed `mixWithWhite` surface set
 * from the design handoff is deferred to a later phase.
 */

import { brandStrong, luminance, softBorder } from "./colorTokens";

// Doc neutral baseline: soft grey primary so accents render as the original look.
const NEUTRAL_PRIMARY = "#5B5C65";

// Returns the brand tint, or the Classic value when the tint is lighter than it.
function floorToNeutral(tinted: string, neutral: string): string {
  return luminance(tinted) > luminance(neutral) ? neutral : tinted;
}

// Card depth — the soft lift cards use to rise off the canvas. Shared by the
// neutral and themed token sets so cards lift identically in both looks.
// Two layers (tight crisp edge + softer wider spread) with NO y-offset so the
// shadow falls equally on all four sides of the card rather than pooling at the
// bottom.
const CARD_SHADOW =
  "0 0 2px rgba(16,40,30,0.05), " +
  "0 0 8px rgba(16,40,30,0.06)";

// Linear blend of `hex` toward white. t = 0 -> hex, t = 1 -> white.
export function mixWithWhite(hex: string, t: number): string {
  const value = hex.replace("#", "");
  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  const mix = (c: number) => Math.round(c + (255 - c) * t);
  const toHex = (c: number) => c.toString(16).padStart(2, "0");
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
}

// Linear blend of `hex` toward black. t = 0 -> hex, t = 1 -> black.
export function mixWithBlack(hex: string, t: number): string {
  const value = hex.replace("#", "");
  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  const mix = (c: number) => Math.round(c * (1 - t));
  const toHex = (c: number) => c.toString(16).padStart(2, "0");
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
}

export function hexToRgba(hex: string, a: number): string {
  const value = hex.replace("#", "");
  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

/**
 * Auto-contrast text/icon color for anything sitting on the brand fill
 * (--p-primary). WCAG relative luminance with a white-biased 0.5 threshold —
 * mirrors miscUtils.getAutoFontColor — so saturated mid-tones (blues/purples)
 * get white while genuinely light brands (e.g. #FFD93D) still get black text.
 */
function onColor(hex: string, branding?: any): string {
  // Preset palettes (any branding whose `name` is not "custom") are designed to
  // pair with white text, so we skip the luminance calc and always return white.
  // Only user-picked "custom" colors fall through to the contrast calculation.
  // Mirrors miscUtils.getAutoFontColor.
  if (branding && branding.name && String(branding.name).toLowerCase() !== "custom") {
    return "#ffffff";
  }
  const value = hex.replace("#", "");
  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  const lin = (c: number) => {
    const n = c / 255;
    return n <= 0.03928 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  // White-biased threshold (0.5, not the strict 0.179 WCAG crossover) so
  // saturated mid-intensity brands resolve to white. See miscUtils.getAutoFontColor.
  return L > 0.5 ? "#000000" : "#ffffff";
}

/**
 * Canonical form for brand-color comparisons. Pure white in any common spelling
 * (`#fff`, `#FFFFFF`, surrounding whitespace) collapses to lowercase `#ffffff`,
 * so every `=== "#ffffff"` brand check across the app treats it as "no brand
 * set" regardless of how the value was stored. Non-white colors are returned
 * unchanged (we deliberately don't rewrite real brand colors).
 */
export function canonicalizeBrandColor(color: any): any {
  if (!color || typeof color !== "string") return color;
  const v = color.trim().replace("#", "").toLowerCase();
  const expanded =
    v.length === 3
      ? v
          .split("")
          .map((c) => c + c)
          .join("")
      : v;
  return expanded === "ffffff" ? "#ffffff" : color;
}

/**
 * True when a brand color is pure white (any spelling) — i.e. the community has
 * effectively set "no brand color" and should fall back to the neutral look.
 */
export function isWhiteColor(color: any): boolean {
  return canonicalizeBrandColor(color) === "#ffffff";
}

/**
 * Neutral === the original app look. True when no brand color is in effect:
 * branding is null/absent, has no primaryColor, or the primaryColor is white
 * (treated identically to "unset" — see isWhiteColor).
 */
export function isNeutralBranding(branding: any): boolean {
  const primary = branding && branding.primaryColor;
  return !primary || isWhiteColor(primary);
}

/**
 * The neutral `--p-*` token set, ready to spread onto the app-shell root via
 * `style={...}`. All values are static per the design handoff.
 */
export function buildNeutralTokens(): Record<string, string> {
  return {
    // Surfaces — chrome stays pure white; only the content area is faint grey.
    "--p-surface": "#FFFFFF",
    "--p-surface-tint": "#F7F8F9",
    "--p-border": "#EBECF0",
    "--p-canvas": "#F7F8F9",
    "--p-card": "#FFFFFF",
    "--p-card-border": "#E7E9EE",
    // Card depth — the two-layer lift cards use to rise off the canvas. Themed
    // communities have no token, so cards fall back to `none` (their look today).
    // Slightly stronger than the original spec values (0.04/0.05) so the lift is
    // actually perceptible against the faint-grey canvas.
    "--p-card-shadow": CARD_SHADOW,
    // Accents — soft grey so buttons/pills/links match the original neutral look.
    "--p-primary": NEUTRAL_PRIMARY,
    "--p-on-primary": onColor(NEUTRAL_PRIMARY),
    "--p-primary-tint": hexToRgba(NEUTRAL_PRIMARY, 0.1),
    "--p-primary-soft": hexToRgba(NEUTRAL_PRIMARY, 0.14),
    // Sidebar hover fill — stronger than soft (0.14) so hover is clearly visible,
    // still well below the solid --p-primary selected fill.
    "--p-primary-hover": hexToRgba(NEUTRAL_PRIMARY, 0.2),
    // Functional accent (progress meter, completion checkmarks) — the neutral
    // theme keeps the classic GroupApp blue here rather than the soft grey, so
    // progress/success indicators read as blue, not grey.
    "--p-accent": "#365DE6",
    "--p-on-accent": "#FFFFFF",
    // Soft accent fill (e.g. an ON toggle's track behind the accent knob). Neutral
    // keeps the classic light-blue that pairs with the #365DE6 knob.
    "--p-accent-soft": "#CCD7FF",
    // Selected-tab treatment — neutral uses an OUTLINE (white fill + dark border +
    // dark label) instead of a grey fill, so the active tab looks intentional on a
    // white/no-theme community.
    "--p-tab-active-bg": "#FFFFFF",
    "--p-tab-active-border": "#202125",
    "--p-tab-active-label": "#202125",
    // Hero gradient stops — derived from the neutral primary.
    "--p-hero-from": mixWithWhite(NEUTRAL_PRIMARY, 0.85),
    "--p-hero-to": mixWithWhite(NEUTRAL_PRIMARY, 0.95),
  };
}

/**
 * The themed `--p-*` token set for a community that chose a brand color, per the
 * handoff doc §3 (`surfaceTokens` themed branch + `paletteTokens`). Surfaces take
 * a very faint brand tint (0.82–0.96 mixed toward white, so even a saturated
 * color resolves to a barely-there off-white); cards stay pure white and lift off
 * the tinted canvas; accents adopt the brand color directly.
 *
 * Note: primary buttons and inline accents are NOT driven by these tokens — the
 * legacy `theme.custom.primaryColor` inline-style path in `miscUtils` already
 * colors them per community (brand color when themed, #365DE6 when white/neutral).
 * This token set only owns the surface layer (chrome, canvas, cards, hairlines,
 * hero) defined by the doc's `surfaceTokens` themed branch.
 */
export function buildThemedTokens(primaryColor: string, branding?: any): Record<string, string> {
  const primary = primaryColor;
  return {
    // Surfaces — faint brand tint; tints and hairlines floor at the Classic greys
    // so a near-white brand color still shows a visible canvas and border.
    "--p-surface": mixWithWhite(primary, 0.96),
    "--p-surface-tint": floorToNeutral(mixWithWhite(primary, 0.91), "#F7F8F9"),
    "--p-border": softBorder(primary, 0.82),
    "--p-canvas": floorToNeutral(mixWithWhite(primary, 0.9), "#F7F8F9"),
    "--p-card": "#FFFFFF",
    "--p-card-border": softBorder(primary, 0.86),
    "--p-card-shadow": CARD_SHADOW,
    // Accents — adopt the brand color directly. Tint/soft match the design
    // guideline (0.10 / 0.14): --p-primary-tint is the faint hover fill,
    // --p-primary-soft the slightly stronger selected/active fill.
    "--p-primary": primary,
    "--p-on-primary": onColor(primary, branding),
    "--p-primary-tint": hexToRgba(primary, 0.10),
    "--p-primary-soft": hexToRgba(primary, 0.14),
    // Sidebar hover fill — stronger than soft (0.14) so hover is clearly visible,
    // still well below the solid --p-primary selected fill.
    "--p-primary-hover": hexToRgba(primary, 0.2),
    // Functional accent + selected-tab treatment — themed communities keep the
    // brand color (brand-filled meter/checkmarks and brand-filled active tab with
    // an auto-contrast label), mirroring the neutral set's structure.
    "--p-accent": primary,
    "--p-on-accent": onColor(primary, branding),
    // Soft accent fill — light brand wash for an ON toggle's track (the knob uses
    // the full brand --p-accent). 0.72 toward white mirrors how #CCD7FF relates to
    // the neutral #365DE6 accent.
    "--p-accent-soft": mixWithWhite(primary, 0.72),
    "--p-tab-active-bg": primary,
    "--p-tab-active-border": "#D5D8DF",
    "--p-tab-active-label": onColor(primary, branding),
    // Themed-ONLY accents (intentionally absent from buildNeutralTokens). These
    // gate surfaces that should stay their original literal color for neutral and
    // token-excluded routes, but pick up the brand for real-brand communities:
    // `var(--p-brand-tint, <original>)`. `--p-brand` is the full brand color (for
    // borders / themed icons), `--p-brand-tint` a faint brand wash (for soft
    // tinted boxes like the trial summary / included add-on cards).
    "--p-brand": primary,
    // Deeper brand shade for small high-contrast chips (e.g. the privilege
    // badge text/icon) where the plain brand color reads too washed-out on a
    // pale tint. Nudged toward black so the label stays crisp and legible.
    "--p-brand-strong": brandStrong(primary),
    "--p-brand-tint": mixWithWhite(primary, 0.92),
    // Soft brand hairline — more visible than the 0.92 tint fill, well short of
    // the full brand color. For bordering tinted boxes (e.g. the trial summary).
    "--p-brand-border": mixWithWhite(primary, 0.72),
    // Hero gradient stops — derived from the brand color.
    "--p-hero-from": mixWithWhite(primary, 0.85),
    "--p-hero-to": mixWithWhite(primary, 0.95),
  };
}

/**
 * The CLASSIC ("flat") token set — the accepted default look. Surfaces stay
 * neutral/flat (white chrome, faint-grey canvas, NO card shadow, NO brand
 * surface wash), but the brand color is applied to FUNCTIONAL elements only:
 * primary buttons, hover/selected fills, accents, selected tabs, notification
 * badges. This is the look an admin gets by default and on Reset.
 *
 * When `primaryColor` is null/white the community has no brand color, so every
 * functional token falls back to the neutral grey set — i.e. it renders exactly
 * like `buildNeutralTokens()` EXCEPT cards are flat (`--p-card-shadow: none`).
 * Contrast with `buildThemedTokens` (the opt-in "Chrome" style), which tints the
 * surfaces and restores the card shadow.
 */
export function buildClassicTokens(primaryColor: string | null, branding?: any): Record<string, string> {
  // Neutral, flat surfaces — never brand-washed in Classic. Card lift removed.
  const base: Record<string, string> = {
    ...buildNeutralTokens(),
    "--p-card-shadow": "none",
  };

  // No brand color → pure neutral-flat (byte-identical to neutral minus shadow).
  if (!primaryColor || isWhiteColor(primaryColor)) {
    return base;
  }

  // Branded Classic: overlay ONLY the functional-brand tokens onto the flat
  // neutral surfaces. Surfaces (--p-surface/-tint/-canvas/-card/-border) and the
  // hero gradient stay neutral (from `base`); the brand shows on buttons, hover
  // fills, accents, selected tabs, and badges.
  const primary = primaryColor;
  return {
    ...base,
    "--p-primary": primary,
    "--p-on-primary": onColor(primary, branding),
    "--p-primary-tint": hexToRgba(primary, 0.1),
    "--p-primary-soft": hexToRgba(primary, 0.14),
    "--p-primary-hover": hexToRgba(primary, 0.2),
    "--p-accent": primary,
    "--p-on-accent": onColor(primary, branding),
    "--p-accent-soft": mixWithWhite(primary, 0.72),
    "--p-tab-active-bg": primary,
    "--p-tab-active-border": "#D5D8DF",
    "--p-tab-active-label": onColor(primary, branding),
    // Brand family — kept in Classic because these back functional HOVER fills
    // (sidebar/course-row/CTA hover) and small high-contrast chips, not surface
    // chrome. The static surface washes that also read --p-brand-tint are minor
    // accents, acceptable on the flat look.
    "--p-brand": primary,
    "--p-brand-strong": brandStrong(primary),
    "--p-brand-tint": mixWithWhite(primary, 0.92),
    "--p-brand-border": mixWithWhite(primary, 0.72),
  };
}

/**
 * Single source of truth for "which token set does this community get?".
 *
 * - `themeStyle: 'chrome'` + a real brand color → the tinted/shadowed "Chrome"
 *   look (`buildThemedTokens`).
 * - everything else (default, missing themeStyle, neutral/white color, or
 *   `themeStyle: 'classic'`) → the flat "Classic" look (`buildClassicTokens`).
 *
 * A missing `themeStyle` resolves to Classic, so existing communities (whose
 * persisted `custom_branding` predates this field) render flat with zero
 * migration.
 */
export function selectThemeTokens(branding: any): Record<string, string> {
  const style = (branding && branding.themeStyle) || "classic";
  const neutral = isNeutralBranding(branding);
  const primary = (branding && branding.primaryColor) || "#365DE6";
  if (style === "chrome" && !neutral) {
    return buildThemedTokens(primary, branding);
  }
  return buildClassicTokens(neutral ? null : primary, branding);
}
